import { Box, CircularProgress, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import * as d3 from 'd3';
import { TriangleAlert } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import oroborosGraphLogo from '../assets/oroboros_logo_graph.png';
import graphBackgroundPattern from '../assets/patterns/dots_6_percent_512px.png';
import { useFileDataStore } from '../stores/fileDataStore.js';
import { useNavigationStore } from '../stores/navigationStore.js';
import './graph.css';

const Graph = ({ height = '100%', controlsRef, chamber = 'A', isActive = false }) => {
  const {
    chamberAData,
    chamberBData,
    chamberAMarks,
    chamberBMarks,
    chamberAEvents,
    chamberBEvents,
    chamberAPlotConfig,
    chamberBPlotConfig,
    isLoading,
    error,
    currentFileMeta,
  } = useFileDataStore();

  // Global navigation store for syncing X-axis between plots
  const {
    syncXTransform,
    syncXDomainOverride,
    setSyncXTransform,
    setSyncXDomainOverride,
    resetSyncNavigation,
    isLiveSyncing,
    setIsLiveSyncing,
  } = useNavigationStore();

  const currentFileData = chamber === 'A' ? chamberAData : chamberBData;
  const currentMarks = chamber === 'A' ? chamberAMarks : chamberBMarks;
  const currentEvents = chamber === 'A' ? chamberAEvents : chamberBEvents;
  const currentPlotConfig = chamber === 'A' ? chamberAPlotConfig : chamberBPlotConfig;

  // Extract plot config for O2 and O2Flux if available
  const o2PlotConfig = useMemo(() => {
    return currentPlotConfig?.find((config) => config.data_type === 'O2') || null;
  }, [currentPlotConfig]);

  const fluxPlotConfig = useMemo(() => {
    return currentPlotConfig?.find((config) => config.data_type === 'O2Flux') || null;
  }, [currentPlotConfig]);

  const containerRef = useRef(null);
  const canvasRef = useRef(null);
  const svgRef = useRef(null);
  const overlaySvgRef = useRef(null);
  const markGradientIdRef = useRef(`mark-gradient-${Math.random().toString(36).slice(2)}`);
  const theme = useTheme();

  const OXYGEN_COLOR = theme.palette.oxygen.oxygenColor;
  const FLUX_COLOR = '#F5426A';

  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

  // X-axis transform synced globally between plots (initialize from store, then sync via subscription)
  const [xTransform, setXTransform] = useState(() => syncXTransform);
  // Y-axis transforms remain local (independent per plot and per data type)
  const [yTransform, setYTransform] = useState({ k: 1, translate: 0 }); // For oxygen
  const [fluxYTransform, setFluxYTransform] = useState({ k: 1, translate: 0 }); // For flux
  // X-axis domain override synced globally (initialize from store, then sync via subscription)
  const [xDomainOverride, setXDomainOverride] = useState(() => syncXDomainOverride);
  // Y-axis domain overrides remain local (independent per plot and per data type)
  const [yDomainOverride, setYDomainOverride] = useState(null); // For oxygen
  const [fluxYDomainOverride, setFluxYDomainOverride] = useState(null); // For flux
  const [cursorInfo, setCursorInfo] = useState(null);

  const rafIdRef = useRef(0);
  const xTransformRef = useRef({ k: 1, translate: 0 });
  const yTransformRef = useRef({ k: 1, translate: 0 });
  const fluxYTransformRef = useRef({ k: 1, translate: 0 });
  const yDomainOverrideRef = useRef(null);
  const fluxYDomainOverrideRef = useRef(null);
  const xDomainOverrideRef = useRef(null);
  const panLockRef = useRef(null); // null | 'x' (lock x → vertical-only) | 'y' (lock y → horizontal-only)
  const zoomEndRafRef = useRef(0); // rAF handle for zoom end updates
  const renderSVGRef = useRef(null); // holds latest renderSVG for zoom end
  const svgBuiltRef = useRef(false); // whether the full SVG has been built for the current size
  const prevDimsRef = useRef({ w: 0, h: 0 }); // last dimensions a full SVG build ran at
  const isDraggingRef = useRef(false); // true during active pan/drag (not wheel zoom)
  const isZoomingRef = useRef(false); // true during wheel zoom (prevents useEffect from updating marks)
  const pendingXTransformRef = useRef(null); // holds x transform during drag without React updates
  const pendingYTransformRef = useRef(null); // holds y transform during drag without React updates
  const pendingFluxYTransformRef = useRef(null); // holds flux y transform during drag without React updates
  const pendingYDomainOverrideRef = useRef(null); // holds y domain override during drag when k=1
  const pendingFluxYDomainOverrideRef = useRef(null); // holds flux y domain override during drag when k=1
  const lastAxisUpdateRef = useRef(0); // timestamp of last axis update (for throttling)
  const updateSVGAxesRef = useRef(null); // stable ref to updateSVGAxes for use in zoom handler
  const dragStartRef = useRef({
    x: 0,
    y: 0,
    xTransform: { k: 1, translate: 0 },
    yTransform: { k: 1, translate: 0 },
    fluxYTransform: { k: 1, translate: 0 },
    yDomainOverride: null, // Store starting Y domain override for vertical panning when k=1
    fluxYDomainOverride: null, // Store starting flux Y domain override for vertical panning when k=1
  });

  // Ref for D3 zoom transition (used for smooth animated zoom)
  const zoomTransitionRef = useRef(null);
  // Target transform for ongoing zoom animation (null when not animating)
  const zoomTargetRef = useRef(null);

  // Data → [{time: Date, oxygen: number, flux?: number}]
  const data = useMemo(() => {
    if (!currentFileData?.timeSeries || !currentFileData?.oxygenSeries) return [];
    const { timeSeries, oxygenSeries, fluxSeries } = currentFileData;
    const n = Math.min(timeSeries.length, oxygenSeries.length);
    return Array.from({ length: n }, (_, i) => {
      const point = {
        time: new Date(timeSeries[i] * 1000),
        oxygen: oxygenSeries[i],
      };
      // Include flux if available
      if (fluxSeries && i < fluxSeries.length) {
        point.flux = fluxSeries[i];
      }
      return point;
    });
  }, [currentFileData]);

  // Layout (memoized to prevent infinite re-render loops)
  const margin = useMemo(() => ({ top: 24, right: 90, bottom: 60, left: 100 }), []);
  const plotWidth = Math.max(0, dimensions.width - margin.left - margin.right);
  const plotHeight = Math.max(0, dimensions.height - margin.top - margin.bottom);

  // Base D3 scales (never transformed directly)
  const baseXScale = useMemo(() => {
    if (!data.length) return d3.scaleUtc().domain([new Date(), new Date()]).range([0, plotWidth]);
    const domain = xDomainOverride ?? d3.extent(data, (d) => d.time);
    return d3.scaleUtc().domain(domain).range([0, plotWidth]);
  }, [data, plotWidth, xDomainOverride]);

  // Base Y scale for oxygen (left axis)
  const baseYScale = useMemo(() => {
    if (!data.length) return d3.scaleLinear().domain([0, 1]).range([plotHeight, 0]);

    if (yDomainOverride) {
      return d3.scaleLinear().domain(yDomainOverride).range([plotHeight, 0]);
    }

    // Use plot_config if available (from dld8 file)
    if (
      o2PlotConfig &&
      typeof o2PlotConfig.ymin === 'number' &&
      typeof o2PlotConfig.ymax === 'number'
    ) {
      return d3
        .scaleLinear()
        .domain([o2PlotConfig.ymin, o2PlotConfig.ymax])
        .nice()
        .range([plotHeight, 0]);
    }

    // Fallback: Use only oxygen values for Y scale domain
    const yMin = d3.min(data, (d) => d.oxygen);
    const yMax = d3.max(data, (d) => d.oxygen);
    const pad = Math.max(1e-6, (yMax - yMin) * 0.05);
    return d3
      .scaleLinear()
      .domain([yMin - pad, yMax + pad])
      .nice()
      .range([plotHeight, 0]);
  }, [data, plotHeight, yDomainOverride, o2PlotConfig]);

  // Base Y scale for flux (right axis) - only created if flux data exists
  const hasFluxData = useMemo(() => data.some((d) => d.flux !== undefined), [data]);
  const baseFluxYScale = useMemo(() => {
    if (!hasFluxData || !data.length) return d3.scaleLinear().domain([0, 1]).range([plotHeight, 0]);

    if (fluxYDomainOverride) {
      return d3.scaleLinear().domain(fluxYDomainOverride).range([plotHeight, 0]);
    }

    // Use plot_config if available (from dld8 file)
    if (
      fluxPlotConfig &&
      typeof fluxPlotConfig.ymin === 'number' &&
      typeof fluxPlotConfig.ymax === 'number'
    ) {
      // When using plot_config, still align zero points if both scales contain zero
      const shouldAlignZeros = !yDomainOverride && !fluxYDomainOverride;

      if (shouldAlignZeros) {
        // Get initial O2 domain (either from plot_config or calculated)
        let initialO2Domain;
        if (
          o2PlotConfig &&
          typeof o2PlotConfig.ymin === 'number' &&
          typeof o2PlotConfig.ymax === 'number'
        ) {
          initialO2Domain = [o2PlotConfig.ymin, o2PlotConfig.ymax];
        } else {
          const yMin = d3.min(data, (d) => d.oxygen);
          const yMax = d3.max(data, (d) => d.oxygen);
          const pad = Math.max(1e-6, (yMax - yMin) * 0.05);
          initialO2Domain = d3
            .scaleLinear()
            .domain([yMin - pad, yMax + pad])
            .nice()
            .domain();
        }
        const initialO2Scale = d3.scaleLinear().domain(initialO2Domain).range([plotHeight, 0]);

        // Align zero points: if both oxygen and flux scales contain zero, align them
        const [o2DomainMin, o2DomainMax] = initialO2Scale.domain();
        const o2ContainsZero = o2DomainMin <= 0 && 0 <= o2DomainMax;
        const fluxContainsZero = fluxPlotConfig.ymin <= 0 && 0 <= fluxPlotConfig.ymax;

        if (o2ContainsZero && fluxContainsZero) {
          // Calculate where zero is on the initial oxygen scale (in pixels, from top)
          const o2ZeroPixel = initialO2Scale(0);

          // We want zero on the flux scale to map to the same pixel position
          const rangeSize = fluxPlotConfig.ymax - fluxPlotConfig.ymin;
          const zeroRatio = (plotHeight - o2ZeroPixel) / plotHeight; // 0 at top, 1 at bottom
          let alignedMin = -zeroRatio * rangeSize;
          let alignedMax = alignedMin + rangeSize;

          // Verify zero is still in the domain
          if (alignedMin > 0 || alignedMax < 0) {
            // If zero would be outside, adjust to include it
            if (alignedMin > 0) {
              alignedMax = alignedMin + rangeSize;
              alignedMin = 0;
            } else {
              alignedMin = alignedMax - rangeSize;
              alignedMax = 0;
            }
          }

          return d3.scaleLinear().domain([alignedMin, alignedMax]).nice().range([plotHeight, 0]);
        }
      }

      // Use plot_config domain directly (no zero alignment needed or not applicable)
      return d3
        .scaleLinear()
        .domain([fluxPlotConfig.ymin, fluxPlotConfig.ymax])
        .nice()
        .range([plotHeight, 0]);
    }

    // Fallback: Use only flux values for flux Y scale domain
    // Zoom in by factor of 120 by default (show 1/120th of the full range, centered)
    const fluxValues = data.map((d) => d.flux).filter((v) => v !== undefined);
    if (fluxValues.length === 0) {
      return d3.scaleLinear().domain([0, 1]).range([plotHeight, 0]);
    }
    const fluxMin = d3.min(fluxValues);
    const fluxMax = d3.max(fluxValues);
    const pad = Math.max(1e-6, (fluxMax - fluxMin) * 0.05);
    const fullRange = fluxMax + pad - (fluxMin - pad);
    const center = (fluxMin + fluxMax) / 2;
    // Zoom in by factor of 120: show 1/120th of the full range centered on the data
    const zoomedRange = fullRange / 120;
    let zoomedMin = center - zoomedRange / 2;
    let zoomedMax = center + zoomedRange / 2;

    // Align zero points ONLY on initial load (when neither scale has been modified by user)
    // Once user starts panning/zooming, stop aligning zeros to allow independent movement
    const shouldAlignZeros = !yDomainOverride && !fluxYDomainOverride;

    if (shouldAlignZeros) {
      // Calculate initial oxygen domain (without override) for zero alignment
      // This ensures we use the original domain, not the modified one after user interaction
      let initialO2Domain;
      if (
        o2PlotConfig &&
        typeof o2PlotConfig.ymin === 'number' &&
        typeof o2PlotConfig.ymax === 'number'
      ) {
        initialO2Domain = [o2PlotConfig.ymin, o2PlotConfig.ymax];
      } else {
        const yMin = d3.min(data, (d) => d.oxygen);
        const yMax = d3.max(data, (d) => d.oxygen);
        const pad = Math.max(1e-6, (yMax - yMin) * 0.05);
        initialO2Domain = d3
          .scaleLinear()
          .domain([yMin - pad, yMax + pad])
          .nice()
          .domain();
      }
      const initialO2Scale = d3.scaleLinear().domain(initialO2Domain).range([plotHeight, 0]);

      // Align zero points: if both oxygen and flux scales contain zero, align them
      const [o2DomainMin, o2DomainMax] = initialO2Scale.domain();
      const o2ContainsZero = o2DomainMin <= 0 && 0 <= o2DomainMax;
      const fluxContainsZero = zoomedMin <= 0 && 0 <= zoomedMax;

      if (o2ContainsZero && fluxContainsZero) {
        // Calculate where zero is on the initial oxygen scale (in pixels, from top)
        const o2ZeroPixel = initialO2Scale(0);

        // We want zero on the flux scale to map to the same pixel position
        // For a D3 scale: pixel = plotHeight + (value - d0) / (d1 - d0) * (0 - plotHeight)
        // For zero: o2ZeroPixel = plotHeight - (d0 / (d1 - d0)) * plotHeight
        // Solving: d0 / (d1 - d0) = (plotHeight - o2ZeroPixel) / plotHeight
        // If we keep the range size constant: d1 - d0 = rangeSize
        // Then: d0 = (plotHeight - o2ZeroPixel) / plotHeight * rangeSize
        // And: d1 = d0 + rangeSize
        const rangeSize = zoomedMax - zoomedMin;
        const zeroRatio = (plotHeight - o2ZeroPixel) / plotHeight; // 0 at top, 1 at bottom
        zoomedMin = -zeroRatio * rangeSize;
        zoomedMax = zoomedMin + rangeSize;

        // Verify zero is still in the domain (it should be since we're centering around it)
        if (zoomedMin > 0 || zoomedMax < 0) {
          // If zero would be outside, adjust to include it
          if (zoomedMin > 0) {
            zoomedMax = zoomedMin + rangeSize;
            zoomedMin = 0;
          } else {
            zoomedMin = zoomedMax - rangeSize;
            zoomedMax = 0;
          }
        }
      }
    }

    return d3.scaleLinear().domain([zoomedMin, zoomedMax]).nice().range([plotHeight, 0]);
  }, [
    data,
    plotHeight,
    fluxYDomainOverride,
    hasFluxData,
    fluxPlotConfig,
    o2PlotConfig,
    yDomainOverride,
  ]);

  // Transformed scales for rendering (using separate x and y transforms)
  const xScale = useMemo(() => {
    const [d0, d1] = baseXScale.domain();
    const [r0, r1] = baseXScale.range();
    // Apply scale and translate: new_domain = (old_domain - translate/k) / k
    const k = xTransform.k;
    const tx = xTransform.translate;
    const newD0 = baseXScale.invert((baseXScale(d0) - tx) / k);
    const newD1 = baseXScale.invert((baseXScale(d1) - tx) / k);
    return d3.scaleUtc().domain([newD0, newD1]).range([r0, r1]);
  }, [baseXScale, xTransform]);

  // Transformed Y scale for oxygen
  const yScale = useMemo(() => {
    const [d0, d1] = baseYScale.domain();
    const [r0, r1] = baseYScale.range();
    const k = yTransform.k;
    const ty = yTransform.translate;
    const newD0 = baseYScale.invert((baseYScale(d0) - ty) / k);
    const newD1 = baseYScale.invert((baseYScale(d1) - ty) / k);
    return d3.scaleLinear().domain([newD0, newD1]).range([r0, r1]);
  }, [baseYScale, yTransform]);

  // Transformed Y scale for flux
  const fluxYScale = useMemo(() => {
    const [d0, d1] = baseFluxYScale.domain();
    const [r0, r1] = baseFluxYScale.range();
    const k = fluxYTransform.k;
    const ty = fluxYTransform.translate;
    const newD0 = baseFluxYScale.invert((baseFluxYScale(d0) - ty) / k);
    const newD1 = baseFluxYScale.invert((baseFluxYScale(d1) - ty) / k);
    return d3.scaleLinear().domain([newD0, newD1]).range([r0, r1]);
  }, [baseFluxYScale, fluxYTransform]);

  // Viewport clipping and downsampling for performance
  const visibleData = useMemo(() => {
    if (!data.length || plotWidth <= 0) return data;

    // Get visible domain from current scales
    const [xMin, xMax] = xScale.domain();

    // Use binary search to find visible range efficiently
    const bisector = d3.bisector((d) => d.time).left;
    const startIdx = Math.max(0, bisector(data, xMin) - 1); // Include 1 point before viewport
    const endIdx = Math.min(data.length, bisector(data, xMax) + 1); // Include 1 point after viewport

    const visibleSlice = data.slice(startIdx, endIdx);

    // Downsample if we have more than 2 points per pixel
    // This prevents rendering unnecessary detail that can't be seen
    const maxPoints = plotWidth * 2;

    if (visibleSlice.length <= maxPoints) {
      return visibleSlice;
    }

    // Simple decimation: keep every Nth point
    const step = Math.ceil(visibleSlice.length / maxPoints);
    const downsampled = [];

    // Always keep first and last points of visible range
    downsampled.push(visibleSlice[0]);

    for (let i = step; i < visibleSlice.length - 1; i += step) {
      downsampled.push(visibleSlice[i]);
    }

    // Always keep the last point
    if (visibleSlice.length > 1) {
      downsampled.push(visibleSlice[visibleSlice.length - 1]);
    }

    return downsampled;
  }, [data, xScale, plotWidth]);

  // Filter marks to only those visible in viewport (for real-time updates)
  const visibleMarks = useMemo(() => {
    if (!Array.isArray(currentMarks) || !currentMarks.length) return [];
    const [xMin, xMax] = xScale.domain();
    const xMinMs = xMin.getTime();
    const xMaxMs = xMax.getTime();

    const visible = currentMarks.filter((m) => {
      const startMs = new Date(m.start).getTime();
      const endMs = new Date(m.end ?? m.start).getTime();
      // Include marks that overlap viewport
      return !(endMs < xMinMs || startMs > xMaxMs);
    });

    return visible;
  }, [currentMarks, xScale]);

  // Filter events to only those visible in viewport (for real-time updates)
  const visibleEvents = useMemo(() => {
    if (!Array.isArray(currentEvents) || !currentEvents.length) return [];
    const [xMin, xMax] = xScale.domain();
    const xMinMs = xMin.getTime();
    const xMaxMs = xMax.getTime();

    const visible = currentEvents.filter((e) => {
      // Handle both Date objects and timestamps
      const eventTime = e.time instanceof Date ? e.time : new Date(e.time);
      const eventMs = eventTime.getTime();
      // Include events within viewport
      return eventMs >= xMinMs && eventMs <= xMaxMs;
    });

    return visible;
  }, [currentEvents, xScale]);

  // Flag to skip reading our own writes (set during our store updates)
  const skipNextSyncRef = useRef(false);
  const lastSyncedXTransformRef = useRef(syncXTransform);
  // rAF handle for passive graph live sync
  const liveSyncRafRef = useRef(0);

  // Sync X-axis transform from global store to local state
  // Only update when the store value actually changes (not from our own writes)
  // Skip syncing during live sync (handled by subscription above) and during drag for active plot
  useEffect(() => {
    // Skip if this is the active plot and we're currently dragging
    // Active plot reads from store only after drag completes (onMouseUp)
    if (isActive && isDraggingRef.current) {
      return;
    }

    // Skip during live sync for passive graphs (handled by subscription above)
    if (!isActive && isLiveSyncing) {
      return;
    }

    // Skip if this update is from ourselves
    if (skipNextSyncRef.current) {
      skipNextSyncRef.current = false;
      return;
    }

    // Only update if value actually differs from what we last synced
    // This prevents infinite loops from floating point precision issues
    const lastSynced = lastSyncedXTransformRef.current;
    if (syncXTransform.k !== lastSynced.k || syncXTransform.translate !== lastSynced.translate) {
      setXTransform(syncXTransform);
      xTransformRef.current = syncXTransform;
      lastSyncedXTransformRef.current = syncXTransform;
    }
  }, [syncXTransform, isActive, isLiveSyncing]);

  // Sync X-axis domain override from global store to local state
  // Skip syncing during drag if this is the active plot (prevents flickering)
  useEffect(() => {
    // Skip if this is the active plot and we're currently dragging
    if (isActive && isDraggingRef.current) {
      return;
    }

    if (syncXDomainOverride !== xDomainOverrideRef.current) {
      setXDomainOverride(syncXDomainOverride);
      xDomainOverrideRef.current = syncXDomainOverride;
    }
  }, [syncXDomainOverride, isActive]);

  // Keep refs in sync for zoom handlers
  useEffect(() => {
    xTransformRef.current = xTransform;
  }, [xTransform]);
  useEffect(() => {
    yTransformRef.current = yTransform;
  }, [yTransform]);
  useEffect(() => {
    fluxYTransformRef.current = fluxYTransform;
  }, [fluxYTransform]);
  useEffect(() => {
    yDomainOverrideRef.current = yDomainOverride;
  }, [yDomainOverride]);
  useEffect(() => {
    fluxYDomainOverrideRef.current = fluxYDomainOverride;
  }, [fluxYDomainOverride]);
  useEffect(() => {
    xDomainOverrideRef.current = xDomainOverride;
  }, [xDomainOverride]);

  // ═══════════════════════════════════════════════════════════════════════════
  // HELPER FUNCTIONS - Canvas rendering & mark positioning
  // ═══════════════════════════════════════════════════════════════════════════

  // Helper: Compute visible & downsampled data for given scales
  const computeVisibleData = useCallback((xScale, data, plotWidth) => {
    if (!data.length || plotWidth <= 0) return data;

    const [xMin, xMax] = xScale.domain();
    const bisector = d3.bisector((d) => d.time).left;
    const startIdx = Math.max(0, bisector(data, xMin) - 1);
    const endIdx = Math.min(data.length, bisector(data, xMax) + 1);
    const visibleSlice = data.slice(startIdx, endIdx);

    const maxPoints = plotWidth * 2;
    if (visibleSlice.length <= maxPoints) return visibleSlice;

    const step = Math.ceil(visibleSlice.length / maxPoints);
    const downsampled = [visibleSlice[0]];
    for (let i = step; i < visibleSlice.length - 1; i += step) {
      downsampled.push(visibleSlice[i]);
    }
    if (visibleSlice.length > 1) {
      downsampled.push(visibleSlice[visibleSlice.length - 1]);
    }
    return downsampled;
  }, []);

  // Helper: Render canvas with given data and scales
  const drawCanvasTrace = useCallback(
    (canvas, renderData, xScale, yScale, fluxYScale) => {
      if (!canvas || !renderData.length || plotWidth <= 0 || plotHeight <= 0) return;

      const ctx = canvas.getContext('2d');
      const DPR = window.devicePixelRatio || 1;

      canvas.width = Math.max(1, Math.floor(dimensions.width * DPR));
      canvas.height = Math.max(1, Math.floor(dimensions.height * DPR));
      canvas.style.width = `${dimensions.width}px`;
      canvas.style.height = `${dimensions.height}px`;

      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.scale(DPR, DPR);
      ctx.clearRect(0, 0, dimensions.width, dimensions.height);

      ctx.save();
      ctx.beginPath();
      ctx.rect(margin.left, margin.top, plotWidth, plotHeight);
      ctx.clip();
      ctx.translate(margin.left, margin.top);

      // Check if flux data is available
      const hasFlux = renderData.some((d) => d.flux !== undefined);

      // Render oxygen trace using oxygen Y scale
      ctx.beginPath();
      ctx.strokeStyle = OXYGEN_COLOR;
      ctx.lineWidth = 2;

      let first = true;
      for (let i = 0; i < renderData.length; i++) {
        const x = xScale(renderData[i].time);
        const y = yScale(renderData[i].oxygen);
        if (Number.isFinite(x) && Number.isFinite(y)) {
          if (first) {
            ctx.moveTo(x, y);
            first = false;
          } else {
            ctx.lineTo(x, y);
          }
        }
      }
      ctx.stroke();

      // Render flux trace if available using flux Y scale
      if (hasFlux && fluxYScale) {
        ctx.beginPath();
        ctx.strokeStyle = FLUX_COLOR;
        ctx.lineWidth = 2;

        first = true;
        for (let i = 0; i < renderData.length; i++) {
          if (renderData[i].flux === undefined) continue;
          const x = xScale(renderData[i].time);
          const y = fluxYScale(renderData[i].flux);
          if (Number.isFinite(x) && Number.isFinite(y)) {
            if (first) {
              ctx.moveTo(x, y);
              first = false;
            } else {
              ctx.lineTo(x, y);
            }
          }
        }
        ctx.stroke();
      }

      ctx.restore();
    },
    [dimensions.width, dimensions.height, plotWidth, plotHeight, margin],
  );

  // Helper: Update mark positions in SVG
  const updateMarkPositions = useCallback((xScale, visibleMarkKeys) => {
    try {
      const svg = d3.select(svgRef.current);
      const g = svg.select('g');
      const marksLayer = g.select('.marks-layer');
      const marksTopLayer = g.select('.marks-top-layer');

      if (marksLayer.empty() || marksTopLayer.empty()) return;

      // Update body marks
      marksLayer.selectAll('g.mark').each(function () {
        const el = d3.select(this);
        const start = Number(el.attr('data-start'));
        const end = Number(el.attr('data-end')) || start;
        const x1 = xScale(new Date(start));
        const x2 = xScale(new Date(end));
        const left = Math.min(x1, x2);
        const width = Math.max(1, Math.abs(x2 - x1));

        // Update rectangle
        el.select('rect').attr('x', left).attr('width', width);

        // Update left and right vertical lines explicitly
        // Select lines by their position (first is left, second is right)
        const lines = el.selectAll('line').nodes();
        if (lines.length >= 2) {
          // Left line (first line)
          d3.select(lines[0]).attr('x1', left).attr('x2', left);
          // Right line (second line)
          d3.select(lines[1])
            .attr('x1', left + width)
            .attr('x2', left + width);
        }

        // Update label position
        el.select('text.mark-label').attr('x', left + 8);
      });

      // Update top badges
      marksTopLayer.selectAll('rect.mark-top').each(function () {
        const el = d3.select(this);
        const start = Number(el.attr('data-start'));
        const end = Number(el.attr('data-end')) || start;
        const x1 = xScale(new Date(start));
        const x2 = xScale(new Date(end));
        const left = Math.min(x1, x2);
        const width = Math.max(2, Math.abs(x2 - x1));
        el.attr('x', left).attr('width', width);
      });
    } catch {}
  }, []);

  // Helper: Update event positions in SVG
  const updateEventPositions = useCallback((xScale, visibleEventKeys) => {
    try {
      const svg = d3.select(svgRef.current);
      const g = svg.select('g');
      const eventsLayer = g.select('.events-layer');

      if (eventsLayer.empty()) return;

      // Update event lines
      eventsLayer.selectAll('line.event-line').each(function () {
        const el = d3.select(this);
        const eventTime = Number(el.attr('data-time'));
        const eventX = xScale(new Date(eventTime));

        if (Number.isFinite(eventX)) {
          el.attr('x1', eventX).attr('x2', eventX);
        }
      });

      // Update event labels (centered on the line) and their backgrounds
      eventsLayer.selectAll('g.event-label-group').each(function () {
        const group = d3.select(this);
        const eventTime = Number(group.attr('data-time'));
        const eventX = xScale(new Date(eventTime));
        if (Number.isFinite(eventX)) {
          const textEl = group.select('text.event-label');
          textEl.attr('x', eventX);

          // Update background rectangle position
          const bbox = textEl.node().getBBox();
          const padding = 4;
          group
            .select('rect.event-label-bg')
            .attr('x', bbox.x - padding)
            .attr('y', bbox.y - padding / 2)
            .attr('width', bbox.width + padding * 2)
            .attr('height', bbox.height + padding);
        }
      });
    } catch {}
  }, []);

  // Subscription-based sync for passive graphs during live panning
  // This updates refs and redraws canvas via rAF WITHOUT triggering React re-renders
  // Placed here because it depends on computeVisibleData, drawCanvasTrace, updateMarkPositions, updateEventPositions
  useEffect(() => {
    // Only passive graphs need this subscription (active graph drives the sync)
    if (isActive) return;

    const unsubscribe = useNavigationStore.subscribe(
      (state) => ({ transform: state.syncXTransform, isLive: state.isLiveSyncing }),
      (current, previous) => {
        const { transform, isLive } = current;
        // Only handle updates during live syncing
        if (!isLive) return;

        // Skip our own writes
        if (skipNextSyncRef.current) {
          skipNextSyncRef.current = false;
          return;
        }

        // Check if transform actually changed
        const lastSynced = lastSyncedXTransformRef.current;
        if (transform.k === lastSynced.k && transform.translate === lastSynced.translate) {
          return;
        }

        // Update refs directly (no React state update = no re-render)
        xTransformRef.current = transform;
        lastSyncedXTransformRef.current = transform;

        // Schedule canvas redraw via rAF
        cancelAnimationFrame(liveSyncRafRef.current);
        liveSyncRafRef.current = requestAnimationFrame(() => {
          // Recompute scales and redraw canvas
          const canvas = canvasRef.current;
          if (!canvas || plotWidth <= 0 || plotHeight <= 0 || !data.length) return;

          // Compute X scale from transform
          const [d0, d1] = baseXScale.domain();
          const k = transform.k;
          const tx = transform.translate;
          const newXd0 = baseXScale.invert((baseXScale(d0) - tx) / k);
          const newXd1 = baseXScale.invert((baseXScale(d1) - tx) / k);
          const tempXScale = d3.scaleUtc().domain([newXd0, newXd1]).range(baseXScale.range());

          // Y scales (use current refs for any domain overrides)
          let tempBaseYScale = baseYScale;
          if (yDomainOverrideRef.current) {
            tempBaseYScale = d3
              .scaleLinear()
              .domain(yDomainOverrideRef.current)
              .range([plotHeight, 0]);
          }
          const yK = yTransformRef.current.k;
          const yTy = yTransformRef.current.translate;
          const [yd0, yd1] = tempBaseYScale.domain();
          const newYd0 = tempBaseYScale.invert((tempBaseYScale(yd0) - yTy) / yK);
          const newYd1 = tempBaseYScale.invert((tempBaseYScale(yd1) - yTy) / yK);
          const tempYScale = d3
            .scaleLinear()
            .domain([newYd0, newYd1])
            .range(tempBaseYScale.range());

          let tempFluxYScale = null;
          if (hasFluxData) {
            let tempBaseFluxYScale = baseFluxYScale;
            if (fluxYDomainOverrideRef.current) {
              tempBaseFluxYScale = d3
                .scaleLinear()
                .domain(fluxYDomainOverrideRef.current)
                .range([plotHeight, 0]);
            }
            const fluxK = fluxYTransformRef.current.k;
            const fluxTy = fluxYTransformRef.current.translate;
            const [fd0, fd1] = tempBaseFluxYScale.domain();
            const newFd0 = tempBaseFluxYScale.invert((tempBaseFluxYScale(fd0) - fluxTy) / fluxK);
            const newFd1 = tempBaseFluxYScale.invert((tempBaseFluxYScale(fd1) - fluxTy) / fluxK);
            tempFluxYScale = d3
              .scaleLinear()
              .domain([newFd0, newFd1])
              .range(tempBaseFluxYScale.range());
          }

          // Compute visible data and draw canvas
          const renderData = computeVisibleData(tempXScale, data, plotWidth);
          drawCanvasTrace(canvas, renderData, tempXScale, tempYScale, tempFluxYScale);

          // Update mark and event positions
          updateMarkPositions(tempXScale, new Set());
          updateEventPositions(tempXScale, new Set());

          // Update axes
          if (updateSVGAxesRef.current) {
            updateSVGAxesRef.current(tempXScale, tempYScale, tempFluxYScale);
          }
        });
      },
      {
        equalityFn: (a, b) =>
          a.transform.k === b.transform.k &&
          a.transform.translate === b.transform.translate &&
          a.isLive === b.isLive,
      },
    );

    return () => {
      unsubscribe();
      cancelAnimationFrame(liveSyncRafRef.current);
    };
  }, [
    isActive,
    baseXScale,
    baseYScale,
    baseFluxYScale,
    plotWidth,
    plotHeight,
    data,
    hasFluxData,
    computeVisibleData,
    drawCanvasTrace,
    updateMarkPositions,
    updateEventPositions,
  ]);

  // ResizeObserver → drives pixel ranges for scales/axes
  // Re-attach when plot container mounts after being conditionally rendered
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const { width, height } = entries[0].contentRect;
      setDimensions({ width: Math.round(width), height: Math.round(height) });
    });
    // Initialize immediately to avoid a frame with width/height = 0
    const rect = el.getBoundingClientRect();
    if (rect.width > 0 && rect.height > 0) {
      setDimensions({ width: Math.round(rect.width), height: Math.round(rect.height) });
    }
    ro.observe(el);
    return () => ro.disconnect();
  }, [currentFileData, isLoading]);

  // ═══════════════════════════════════════════════════════════════════════════
  // RENDERING - Canvas & SVG
  // ═══════════════════════════════════════════════════════════════════════════

  // Canvas rendering (used by wheel zoom path via useEffect)
  const renderCanvas = useCallback(() => {
    drawCanvasTrace(canvasRef.current, visibleData, xScale, yScale, fluxYScale);
  }, [visibleData, xScale, yScale, fluxYScale, drawCanvasTrace]);

  // Lightweight update: Only update axes/grids/labels without rebuilding entire SVG
  const updateSVGAxes = useCallback(
    (xScale, yScale, fluxYScale) => {
      const svgEl = svgRef.current;
      if (!svgEl) return;

      const svg = d3.select(svgEl);
      const g = svg.select('g');
      if (g.empty()) return; // SVG not initialized yet

      // Update X-axis
      const xAxis = d3.axisBottom(xScale).tickFormat(d3.utcFormat('%H:%M:%S'));
      const xAxisGroup = g.select('.x-axis');
      if (!xAxisGroup.empty()) {
        xAxisGroup.call(xAxis);
        xAxisGroup.selectAll('.tick text').attr('dy', '12px');
      }

      // Update Y-axes (left shows oxygen, right shows flux if available)
      const yAxis = d3.axisLeft(yScale).ticks(3);
      const yAxisGroups = g.selectAll('.y-axis');
      yAxisGroups.each(function (d, i) {
        const el = d3.select(this);
        if (i === 0) {
          // Left axis - oxygen
          el.call(yAxis);
          el.selectAll('.tick text').attr('dx', '-4px');
        } else if (i === 1 && fluxYScale) {
          // Right axis - flux
          const fluxYAxisRight = d3.axisRight(fluxYScale).ticks(3);
          el.call(fluxYAxisRight);
          el.selectAll('.tick text').attr('dx', '4px');
        } else {
          // Fallback: right axis shows oxygen if no flux
          const yAxisRight = d3.axisRight(yScale).ticks(3);
          el.call(yAxisRight);
          el.selectAll('.tick text').attr('dx', '4px');
        }
      });

      // Update X-grid
      const xGrid = d3.axisBottom(xScale).tickSize(-plotHeight).tickFormat('').ticks(10);
      const xGridGroup = g.select('.x-grid');
      if (!xGridGroup.empty()) {
        xGridGroup.call(xGrid);
      }

      // Update Y-grid (rebuild custom grid lines at tick positions only)
      const yGridGroup = g.select('.y-grid-custom');
      if (!yGridGroup.empty()) {
        yGridGroup.selectAll('line').remove();
        // Get tick values from the Y-axis (matches the 3 ticks we configured)
        const yAxisTemp = d3.axisLeft(yScale).ticks(3);
        const tickValues = yAxisTemp.scale().ticks ? yAxisTemp.scale().ticks(3) : yScale.ticks(3);

        tickValues.forEach((v) => {
          const yPos = yScale(v);
          if (Number.isFinite(yPos) && yPos >= 0 && yPos <= plotHeight) {
            yGridGroup
              .append('line')
              .attr('x1', 0)
              .attr('y1', yPos)
              .attr('x2', plotWidth)
              .attr('y2', yPos);
          }
        });
      }
    },
    [plotWidth, plotHeight],
  );

  // SVG axes + labels (no cursor visuals here)
  const renderSVG = useCallback(() => {
    const svgEl = svgRef.current;
    if (!svgEl) return;

    const svg = d3.select(svgEl);
    svg.selectAll('*').remove();

    svg
      .attr('width', dimensions.width)
      .attr('height', dimensions.height)
      .style('user-select', 'none')
      .style('cursor', 'default');

    const clipId = 'plot-clip-id';
    const groupClipId = 'plot-group-clip-id';
    const topClipId = 'plot-top-clip-id';
    const defs = svg.append('defs');

    defs
      .append('clipPath')
      .attr('id', clipId)
      .append('rect')
      .attr('width', plotWidth)
      .attr('height', plotHeight)
      .attr('x', margin.left)
      .attr('y', margin.top);

    // Clip-path for elements rendered within the translated group (local coords)
    defs
      .append('clipPath')
      .attr('id', groupClipId)
      .append('rect')
      .attr('width', plotWidth)
      .attr('height', plotHeight)
      .attr('x', 0)
      .attr('y', 0);

    // Clip-path for top badges: restrict horizontally to plot and allow a small band above
    const topBand = 12; // pixels above plot to allow for badge drawing
    defs
      .append('clipPath')
      .attr('id', topClipId)
      .append('rect')
      .attr('width', plotWidth)
      .attr('height', topBand)
      .attr('x', 0)
      .attr('y', -topBand);

    svg
      .append('rect')
      .attr('class', 'base-background')
      .attr('width', plotWidth)
      .attr('height', plotHeight)
      .attr('x', margin.left)
      .attr('y', margin.top)
      .attr('clip-path', `url(#${clipId})`);

    // Oroboros Logo, centered in the plot
    svg
      .append('image')
      .attr('xlink:href', oroborosGraphLogo)
      .attr('width', 290)
      .attr('height', 282)
      .attr('x', margin.left + plotWidth / 2 - 290 / 2)
      .attr('y', margin.top + plotHeight / 2 - 282 / 2)
      .attr('clip-path', `url(#${clipId})`);

    const pattern = defs
      .append('pattern')
      .attr('id', 'background-pattern')
      .attr('patternUnits', 'userSpaceOnUse')
      .attr('width', 512)
      .attr('height', 512);
    // Gradient for marks fill
    const markGradient = defs
      .append('linearGradient')
      .attr('id', markGradientIdRef.current)
      .attr('x1', '0%')
      .attr('y1', '0%')
      .attr('x2', '0%')
      .attr('y2', '100%');
    markGradient
      .append('stop')
      .attr('offset', '0%')
      .attr('stop-color', '#CB1045')
      .attr('stop-opacity', 0.13);
    markGradient
      .append('stop')
      .attr('offset', '100%')
      .attr('stop-color', '#FFAAC3')
      .attr('stop-opacity', 0.05);

    pattern
      .append('image')
      .attr('xlink:href', graphBackgroundPattern)
      .attr('width', 512)
      .attr('height', 512);

    svg
      .append('rect')
      .attr('width', plotWidth)
      .attr('height', plotHeight)
      .attr('x', margin.left)
      .attr('y', margin.top)
      .attr('fill', 'url(#background-pattern)')
      .attr('clip-path', `url(#${clipId})`);

    const xAxis = d3.axisBottom(xScale).tickFormat(d3.utcFormat('%H:%M:%S'));
    const yAxis = d3.axisLeft(yScale).ticks(3);
    const yAxisRight = d3.axisRight(yScale).ticks(3);

    const g = svg.append('g').attr('transform', `translate(${margin.left},${margin.top})`);

    g.append('g')
      .attr('class', 'x-axis')
      .attr('transform', `translate(0,${plotHeight})`)
      .call(xAxis)
      .selectAll('.tick text')
      .attr('dy', '12px');

    g.append('g').attr('class', 'y-axis').call(yAxis).selectAll('.tick text').attr('dx', '-4px');

    // Right Y-axis: use flux scale if available, otherwise oxygen scale
    const rightYAxis = hasFluxData && fluxYScale ? d3.axisRight(fluxYScale).ticks(3) : yAxisRight;
    g.append('g')
      .attr('class', 'y-axis')
      .attr('transform', `translate(${plotWidth},0)`)
      .call(rightYAxis)
      .selectAll('.tick text')
      .attr('dx', '4px');

    // Y-axis labels (left and right)
    g.append('text')
      .attr('class', 'y-axis-label')
      .attr('transform', `translate(${-margin.left + 24}, ${plotHeight / 2}) rotate(-90)`)
      .attr('text-anchor', 'middle')
      .text('O₂ concentration [µM]');

    g.append('text')
      .attr('class', 'y-axis-label')
      .attr(
        'transform',
        `translate(${plotWidth + margin.right - 4}, ${plotHeight / 2}) rotate(-90)`,
      )
      .attr('text-anchor', 'middle')
      .text(hasFluxData ? 'O₂ flux per V [ pmol·s⁻¹·mL⁻¹]' : 'O₂ concentration [µM]');

    const xGrid = d3.axisBottom(xScale).tickSize(-plotHeight).tickFormat('').ticks(10);

    g.append('g')
      .attr('class', 'x-grid')
      .attr('transform', `translate(0,${plotHeight})`)
      .call(xGrid);

    const yGridGroup = g.append('g').attr('class', 'y-grid-custom');
    // Get tick values from the Y-axis (matches the 3 ticks we configured)
    const tickValues = yScale.ticks(3);

    tickValues.forEach((v) => {
      const yPos = yScale(v);
      if (Number.isFinite(yPos) && yPos >= 0 && yPos <= plotHeight) {
        yGridGroup
          .append('line')
          .attr('x1', 0)
          .attr('y1', yPos)
          .attr('x2', plotWidth)
          .attr('y2', yPos);
      }
    });

    // Render marks layer (behind overlay)
    const marksLayer = g
      .append('g')
      .attr('class', 'marks-layer')
      .attr('clip-path', `url(#${groupClipId})`);
    // Layer for top badges outside of plot area (clipped to a top band)
    const marksTopLayer = g
      .append('g')
      .attr('class', 'marks-top-layer')
      .attr('clip-path', `url(#${topClipId})`);

    if (Array.isArray(currentMarks) && currentMarks.length > 0) {
      currentMarks.forEach((m, idx) => {
        try {
          const startDate = m.start instanceof Date ? m.start : new Date(m.start);
          const endDate = m.end instanceof Date ? (m.end ?? m.start) : new Date(m.end ?? m.start);
          const x1 = xScale(startDate);
          const x2 = xScale(endDate);
          const left = Math.min(x1, x2);
          const width = Math.max(1, Math.abs(x2 - x1));

          const startMs = startDate.getTime();
          const endMs = endDate.getTime();
          const group = marksLayer
            .append('g')
            .attr('class', 'mark')
            .attr('data-start', startMs)
            .attr('data-end', endMs)
            .attr('data-index', idx);
          group
            .append('rect')
            .attr('x', left)
            .attr('y', 0)
            .attr('width', width)
            .attr('height', plotHeight)
            .attr('fill', `url(#${markGradientIdRef.current})`);

          // Left and right stroke lines
          group
            .append('line')
            .attr('x1', left)
            .attr('x2', left)
            .attr('y1', 0)
            .attr('y2', plotHeight)
            .attr('stroke', '#F5426A')
            .attr('stroke-width', 1);
          group
            .append('line')
            .attr('x1', left + width)
            .attr('x2', left + width)
            .attr('y1', 0)
            .attr('y2', plotHeight)
            .attr('stroke', '#F5426A')
            .attr('stroke-width', 1);

          if (m.label) {
            group
              .append('text')
              .attr('class', 'mark-label')
              .attr('x', left + 8)
              .attr('y', Math.max(12, plotHeight - 8))

              .text(String(m.label));
          }

          // rectangular badge on top outside the plot area
          const topWidth = Math.max(2, width);
          const topHeight = 4;
          const topX = left;
          const topY = -topHeight;
          marksTopLayer
            .append('rect')
            .attr('class', 'mark-top')
            .attr('data-start', startMs)
            .attr('data-end', endMs)
            .attr('data-index', idx)
            .attr('x', topX)
            .attr('y', topY)
            .attr('width', topWidth)
            .attr('height', topHeight)
            .attr('fill', '#F5426A')
            .attr('stroke', '#F5426A')
            .attr('opacity', 1);
        } catch {}
      });
    }

    // Render events layer (black vertical lines for single time point events)
    const eventsLayer = g
      .append('g')
      .attr('class', 'events-layer')
      .attr('clip-path', `url(#${groupClipId})`);

    if (Array.isArray(currentEvents) && currentEvents.length > 0) {
      currentEvents.forEach((e, idx) => {
        try {
          const eventTime = e.time instanceof Date ? e.time : new Date(e.time);
          if (isNaN(eventTime.getTime())) return;

          const eventX = xScale(eventTime);
          if (!Number.isFinite(eventX)) return;

          const eventMs = eventTime.getTime();

          // Vertical line for event (black)
          eventsLayer
            .append('line')
            .attr('class', 'event-line')
            .attr('data-time', eventMs)
            .attr('data-index', idx)
            .attr('x1', eventX)
            .attr('x2', eventX)
            .attr('y1', 0)
            .attr('y2', plotHeight)
            .attr('stroke', '#000000')
            .attr('stroke-width', 1)
            .attr('opacity', 0.7);

          // Event name label centered on the line, positioned slightly down from top
          if (e.name) {
            const labelGroup = eventsLayer
              .append('g')
              .attr('class', 'event-label-group')
              .attr('data-time', eventMs)
              .attr('data-index', idx);

            // Create text first to measure it
            const textNode = labelGroup
              .append('text')
              .attr('class', 'event-label')
              .attr('x', eventX)
              .attr('y', 16)
              .attr('fill', '#000000')
              .attr('font-size', '10px')
              .attr('opacity', 0.9)
              .attr('text-anchor', 'middle')
              .text(String(e.name));

            // Get text bounding box to size the background (with small delay to ensure rendering)
            try {
              const bbox = textNode.node().getBBox();
              const padding = 4;

              // Insert background rectangle before text so it renders behind
              labelGroup
                .insert('rect', 'text')
                .attr('class', 'event-label-bg')
                .attr('x', bbox.x - padding)
                .attr('y', bbox.y - padding / 2)
                .attr('width', bbox.width + padding * 2)
                .attr('height', bbox.height + padding)
                .attr('fill', '#FFFFFF')
                .attr('opacity', 0.9)
                .attr('stroke', 'none');
            } catch {
              // Fallback: use estimated size if bbox not available yet
              const estimatedWidth = e.name.length * 6; // Rough estimate: ~6px per character
              const estimatedHeight = 12;
              const padding = 4;
              labelGroup
                .insert('rect', 'text')
                .attr('class', 'event-label-bg')
                .attr('x', eventX - estimatedWidth / 2 - padding)
                .attr('y', 14 - estimatedHeight / 2 - padding / 2)
                .attr('width', estimatedWidth + padding * 2)
                .attr('height', estimatedHeight + padding)
                .attr('fill', '#FFFFFF')
                .attr('opacity', 0.9)
                .attr('stroke', 'none');
            }
          }
        } catch {}
      });
    }

    // Overlay rect for pointer-events (no D3 event handlers - handled at root SVG)
    g.append('rect')
      .attr('class', 'overlay')
      .attr('width', plotWidth)
      .attr('height', plotHeight)
      .style('fill', 'none')
      .style('pointer-events', 'all')
      .style('cursor', 'default');
  }, [
    dimensions.width,
    dimensions.height,
    margin,
    plotWidth,
    plotHeight,
    xScale,
    yScale,
    fluxYScale,
    hasFluxData,
    data,
    currentMarks,
    currentEvents,
  ]);

  // Keep stable references to render functions for use in event handlers
  useEffect(() => {
    renderSVGRef.current = renderSVG;
  }, [renderSVG]);

  useEffect(() => {
    updateSVGAxesRef.current = updateSVGAxes;
  }, [updateSVGAxes]);

  // Top overlay SVG just for cursor visuals
  const renderOverlaySVG = useCallback(() => {
    const svgEl = overlaySvgRef.current;
    if (!svgEl) return;

    const svg = d3.select(svgEl);
    svg.selectAll('*').remove();

    svg
      .attr('width', dimensions.width)
      .attr('height', dimensions.height)
      .style('pointer-events', 'none');

    if (!cursorInfo) return;

    // Vertical cursor line across plot area
    svg
      .append('line')
      .attr('x1', margin.left + cursorInfo.x)
      .attr('x2', margin.left + cursorInfo.x)
      .attr('y1', margin.top)
      .attr('y2', margin.top + plotHeight)
      .attr('class', 'cursor-line');

    // Cursor labels box and texts
    const hasFlux = cursorInfo.flux !== undefined;
    const labelWidth = 130;
    const labelHeight = hasFlux ? 63 : 45;
    const padding = 20;
    const showOnLeft = cursorInfo.x + labelWidth + padding > plotWidth;
    const labelX = showOnLeft
      ? Math.max(0, cursorInfo.x - labelWidth - padding)
      : cursorInfo.x + padding;
    const adjustedX = labelX + margin.left;
    const adjustedY = margin.top + cursorInfo.y;

    svg
      .append('rect')
      .attr('class', 'cursor-label-bg')
      .attr('x', adjustedX)
      .attr('y', adjustedY)
      .attr('width', labelWidth)
      .attr('height', labelHeight);

    const timeStr = d3.utcFormat('%H:%M:%S')(cursorInfo.time);
    const oxygenStr = cursorInfo.oxygen.toFixed(4);
    const textX = adjustedX + 8;

    svg
      .append('text')
      .attr('class', 'cursor-label-time')
      .attr('x', textX)
      .attr('y', adjustedY + 18)
      .text(`Time: ${timeStr}`);

    svg
      .append('text')
      .attr('class', 'cursor-label-oxygen')
      .attr('x', textX)
      .attr('y', adjustedY + 36)
      .text(`O₂ [µM]: ${oxygenStr}`);

    if (hasFlux) {
      const fluxStr = cursorInfo.flux.toFixed(4);
      svg
        .append('text')
        .attr('class', 'cursor-label-flux')
        .attr('x', textX)
        .attr('y', adjustedY + 54)
        .text(`Flux: ${fluxStr}`);
    }
  }, [dimensions, margin, plotWidth, plotHeight, cursorInfo]);

  // ═══════════════════════════════════════════════════════════════════════════
  // ZOOM & PAN BEHAVIOR - Custom implementation with axis-specific zoom
  // ═══════════════════════════════════════════════════════════════════════════

  useEffect(() => {
    const svgEl = svgRef.current;
    if (!svgEl || plotWidth <= 0 || plotHeight <= 0) return;

    const svg = d3.select(svgEl);

    // Helper: Detect which region mouse is over
    const getRegion = (clientX, clientY) => {
      const rect = svgEl.getBoundingClientRect();
      const x = clientX - rect.left;
      const y = clientY - rect.top;
      const withinPlotX = x >= margin.left && x <= margin.left + plotWidth;
      const withinPlotY = y >= margin.top && y <= margin.top + plotHeight;
      const inXAxisBand =
        withinPlotX && y >= margin.top + plotHeight && y <= margin.top + plotHeight + margin.bottom;
      const inYLeftBand = x >= 0 && x <= margin.left && withinPlotY;
      const inYRightBand = x >= margin.left + plotWidth && x <= dimensions.width && withinPlotY;
      const inPlot = withinPlotX && withinPlotY;

      if (inXAxisBand) return 'x-axis';
      if (inYLeftBand) return 'y-axis-left'; // Oxygen axis
      if (inYRightBand && hasFluxData) return 'y-axis-right'; // Flux axis
      if (inYRightBand) return 'y-axis-left'; // Fallback to oxygen if no flux
      if (inPlot) return 'plot';
      return 'outside';
    };

    // Helper: Apply separate scale transforms to base scales
    const computeScales = (xTrans, yTrans) => {
      return computeScalesWithBase(xTrans, yTrans, baseXScale, baseYScale);
    };

    // Helper: Apply transforms with custom base scales (for domain override during drag)
    const computeScalesWithBase = (xTrans, yTrans, baseX, baseY) => {
      const [xd0, xd1] = baseX.domain();
      const [xr0, xr1] = baseX.range();
      const newXd0 = baseX.invert((baseX(xd0) - xTrans.translate) / xTrans.k);
      const newXd1 = baseX.invert((baseX(xd1) - xTrans.translate) / xTrans.k);
      const tempXScale = d3.scaleUtc().domain([newXd0, newXd1]).range([xr0, xr1]);

      const [yd0, yd1] = baseY.domain();
      const [yr0, yr1] = baseY.range();
      const newYd0 = baseY.invert((baseY(yd0) - yTrans.translate) / yTrans.k);
      const newYd1 = baseY.invert((baseY(yd1) - yTrans.translate) / yTrans.k);
      const tempYScale = d3.scaleLinear().domain([newYd0, newYd1]).range([yr0, yr1]);

      return { tempXScale, tempYScale };
    };

    // Helper: Compute flux scale with custom base
    const computeFluxScaleWithBase = (xTrans, fluxYTrans, baseX, baseFluxY) => {
      const [xd0, xd1] = baseX.domain();
      const [xr0, xr1] = baseX.range();
      const newXd0 = baseX.invert((baseX(xd0) - xTrans.translate) / xTrans.k);
      const newXd1 = baseX.invert((baseX(xd1) - xTrans.translate) / xTrans.k);
      const tempXScale = d3.scaleUtc().domain([newXd0, newXd1]).range([xr0, xr1]);

      const [yd0, yd1] = baseFluxY.domain();
      const [yr0, yr1] = baseFluxY.range();
      const newYd0 = baseFluxY.invert((baseFluxY(yd0) - fluxYTrans.translate) / fluxYTrans.k);
      const newYd1 = baseFluxY.invert((baseFluxY(yd1) - fluxYTrans.translate) / fluxYTrans.k);
      const tempFluxYScale = d3.scaleLinear().domain([newYd0, newYd1]).range([yr0, yr1]);

      return { tempXScale, tempFluxYScale };
    };

    // Wheel zoom handler (axis-specific when over axes, even when over plot)
    // Uses D3 transitions for smooth animated zoom
    // Only allow navigation if this plot is active
    const ZOOM_TRANSITION_MS = 400; // Duration of zoom animation in milliseconds
    const ZOOM_SCALE_STEP = 1.8; // Zoom factor per wheel step (1.1 = 10% per step)

    const onWheel = (event) => {
      if (!isActive) return; // Only active plot can navigate
      event.preventDefault();
      const region = getRegion(event.clientX, event.clientY);
      if (region === 'outside') return;

      const rect = svgEl.getBoundingClientRect();
      const mouseX = event.clientX - rect.left - margin.left; // local plot coords
      const mouseY = event.clientY - rect.top - margin.top;

      const delta = -event.deltaY;
      const scaleFactor = delta > 0 ? ZOOM_SCALE_STEP : 1 / ZOOM_SCALE_STEP;

      // Use current target (if animating) or current ref values as base for new target
      const baseXTrans = zoomTargetRef.current?.x ?? { ...xTransformRef.current };
      const baseYTrans = zoomTargetRef.current?.y ?? { ...yTransformRef.current };
      const baseFluxYTrans = zoomTargetRef.current?.fluxY ?? { ...fluxYTransformRef.current };

      const newXTrans = { ...baseXTrans };
      const newYTrans = { ...baseYTrans };
      const newFluxYTrans = { ...baseFluxYTrans };

      if (region === 'x-axis') {
        // Zoom only X axis
        const oldK = newXTrans.k;
        newXTrans.k = Math.max(1, Math.min(200, oldK * scaleFactor));
        // Zoom towards mouse position
        newXTrans.translate = mouseX - (mouseX - newXTrans.translate) * (newXTrans.k / oldK);
        // Constrain translation (prevent panning left beyond start of data, allow panning right beyond end)
        const maxTx = 0; // Prevent panning left beyond start
        const minTx = plotWidth * (1 - newXTrans.k); // Prevent panning too far left when zoomed in
        // Allow negative translate values (panning right) but enforce minTx when it's positive
        if (minTx > 0) {
          newXTrans.translate = Math.max(minTx, Math.min(maxTx, newXTrans.translate));
        } else {
          // When zoomed in, minTx is negative, so only enforce maxTx (prevent left panning)
          newXTrans.translate = Math.min(maxTx, newXTrans.translate);
        }
      } else if (region === 'y-axis-left') {
        // Zoom only oxygen Y axis - keep X transform unchanged
        // Allow k to go below 1 to enable zooming out beyond initial scale (squeezing)
        const oldK = newYTrans.k;
        newYTrans.k = Math.max(0.01, Math.min(200, oldK * scaleFactor));
        // Zoom towards mouse position
        newYTrans.translate = mouseY - (mouseY - newYTrans.translate) * (newYTrans.k / oldK);
        // Constrain translation vertically
        const maxTy = 0;
        const minTy = plotHeight * (1 - newYTrans.k);
        newYTrans.translate = Math.max(minTy, Math.min(maxTy, newYTrans.translate));
      } else if (region === 'y-axis-right') {
        // Zoom only flux Y axis - keep X transform unchanged
        // Allow k to go below 1 to enable zooming out beyond initial scale (squeezing)
        const oldK = newFluxYTrans.k;
        newFluxYTrans.k = Math.max(0.01, Math.min(200, oldK * scaleFactor));
        // Zoom towards mouse position
        newFluxYTrans.translate =
          mouseY - (mouseY - newFluxYTrans.translate) * (newFluxYTrans.k / oldK);
        // Constrain translation vertically
        const maxTy = 0;
        const minTy = plotHeight * (1 - newFluxYTrans.k);
        newFluxYTrans.translate = Math.max(minTy, Math.min(maxTy, newFluxYTrans.translate));
      } else if (region === 'plot') {
        // Zoom only X axis when zooming in plot area (Y axes remain static, like flux)
        const oldKx = newXTrans.k;
        newXTrans.k = Math.max(1, Math.min(200, oldKx * scaleFactor));

        // Zoom towards mouse position (X only)
        newXTrans.translate = mouseX - (mouseX - newXTrans.translate) * (newXTrans.k / oldKx);

        // Constrain translation (prevent panning left beyond start of data, allow panning right beyond end)
        const maxTx = 0; // Prevent panning left beyond start
        const minTx = plotWidth * (1 - newXTrans.k); // Prevent panning too far left when zoomed in
        // Allow negative translate values (panning right) but enforce minTx when it's positive
        if (minTx > 0) {
          newXTrans.translate = Math.max(minTx, Math.min(maxTx, newXTrans.translate));
        } else {
          // When zoomed in, minTx is negative, so only enforce maxTx (prevent left panning)
          newXTrans.translate = Math.min(maxTx, newXTrans.translate);
        }

        // Y transforms remain unchanged (O2 and flux Y-axes stay static)
      }

      // Store the target transforms
      zoomTargetRef.current = { x: newXTrans, y: newYTrans, fluxY: newFluxYTrans };

      // Capture starting values for interpolation (current actual position)
      const startXTrans = { ...xTransformRef.current };
      const startYTrans = { ...yTransformRef.current };
      const startFluxYTrans = { ...fluxYTransformRef.current };

      // Signal that live syncing is starting (passive graph will follow)
      setIsLiveSyncing(true);

      // Helper function to render at a given interpolated state
      const renderAtTransform = (interpX, interpY, interpFluxY) => {
        // Update refs
        xTransformRef.current = interpX;
        yTransformRef.current = interpY;
        fluxYTransformRef.current = interpFluxY;

        // Use current domain override if it exists (from previous panning)
        let tempBaseYScale = baseYScale;
        if (yDomainOverrideRef.current) {
          tempBaseYScale = d3
            .scaleLinear()
            .domain(yDomainOverrideRef.current)
            .range([plotHeight, 0]);
        }

        let tempBaseFluxYScale = baseFluxYScale;
        if (fluxYDomainOverrideRef.current) {
          tempBaseFluxYScale = d3
            .scaleLinear()
            .domain(fluxYDomainOverrideRef.current)
            .range([plotHeight, 0]);
        }

        const { tempXScale, tempYScale } = computeScalesWithBase(
          interpX,
          interpY,
          baseXScale,
          tempBaseYScale,
        );
        const { tempFluxYScale } = computeFluxScaleWithBase(
          interpX,
          interpFluxY,
          baseXScale,
          tempBaseFluxYScale,
        );
        const renderData = computeVisibleData(tempXScale, data, plotWidth);
        drawCanvasTrace(canvasRef.current, renderData, tempXScale, tempYScale, tempFluxYScale);

        // Update visible marks
        const [xMin, xMax] = tempXScale.domain();
        const xMinMs = xMin.getTime();
        const xMaxMs = xMax.getTime();
        const visibleMarkKeys = new Set();
        if (Array.isArray(currentMarks)) {
          currentMarks.forEach((m) => {
            const startMs = new Date(m.start).getTime();
            const endMs = new Date(m.end ?? m.start).getTime();
            if (!(endMs < xMinMs || startMs > xMaxMs)) {
              visibleMarkKeys.add(`${startMs}-${endMs}`);
            }
          });
        }
        updateMarkPositions(tempXScale, visibleMarkKeys);

        // Update event positions
        const visibleEventKeys = new Set();
        if (Array.isArray(currentEvents)) {
          currentEvents.forEach((e) => {
            const eventTime = e.time instanceof Date ? e.time : new Date(e.time);
            const eventMs = eventTime.getTime();
            if (eventMs >= xMinMs && eventMs <= xMaxMs) {
              visibleEventKeys.add(`${eventMs}`);
            }
          });
        }
        updateEventPositions(tempXScale, visibleEventKeys);

        if (updateSVGAxesRef.current) {
          updateSVGAxesRef.current(tempXScale, tempYScale, tempFluxYScale);
        }

        // Update global store for X-axis (synced between plots)
        skipNextSyncRef.current = true;
        setSyncXTransform(interpX);
      };

      // Create or reuse a D3 selection for the transition
      if (!zoomTransitionRef.current) {
        zoomTransitionRef.current = d3.select(document.createElement('div'));
      }

      // Interrupt any ongoing zoom transition and start a new one
      zoomTransitionRef.current
        .interrupt('zoom')
        .transition('zoom')
        .duration(ZOOM_TRANSITION_MS)
        .ease(d3.easeCubicOut)
        .tween('zoom', () => {
          // Create interpolators for each transform
          const interpXK = d3.interpolate(startXTrans.k, newXTrans.k);
          const interpXTx = d3.interpolate(startXTrans.translate, newXTrans.translate);
          const interpYK = d3.interpolate(startYTrans.k, newYTrans.k);
          const interpYTy = d3.interpolate(startYTrans.translate, newYTrans.translate);
          const interpFluxYK = d3.interpolate(startFluxYTrans.k, newFluxYTrans.k);
          const interpFluxYTy = d3.interpolate(startFluxYTrans.translate, newFluxYTrans.translate);

          return (t) => {
            const interpX = { k: interpXK(t), translate: interpXTx(t) };
            const interpY = { k: interpYK(t), translate: interpYTy(t) };
            const interpFluxY = { k: interpFluxYK(t), translate: interpFluxYTy(t) };

            renderAtTransform(interpX, interpY, interpFluxY);
          };
        })
        .on('end', () => {
          // Animation complete - clear target and sync final state
          zoomTargetRef.current = null;
          setIsLiveSyncing(false);

          // Final React state updates
          setXTransform(newXTrans);
          setYTransform(newYTrans);
          setFluxYTransform(newFluxYTrans);
        })
        .on('interrupt', () => {
          // Animation was interrupted by a new wheel event
          // Don't clear target or stop syncing - the new animation will continue
        });
    };

    // Mouse drag handler for panning
    // Only allow navigation if this plot is active
    const onMouseDown = (event) => {
      if (!isActive) return; // Only active plot can navigate
      const region = getRegion(event.clientX, event.clientY);
      if (region === 'outside') return;

      event.preventDefault();
      setCursorInfo(null);
      isDraggingRef.current = true;
      setIsLiveSyncing(true); // Signal that live sync is in progress

      // Store drag start state in ref
      dragStartRef.current = {
        x: event.clientX,
        y: event.clientY,
        xTransform: { ...xTransformRef.current },
        yTransform: { ...yTransformRef.current },
        fluxYTransform: { ...fluxYTransformRef.current },
        yDomainOverride: yDomainOverrideRef.current, // Store starting Y domain override for vertical panning
        fluxYDomainOverride: fluxYDomainOverrideRef.current, // Store starting flux Y domain override
      };

      // Determine pan lock based on region
      panLockRef.current = null;
      if (region === 'x-axis') {
        panLockRef.current = 'y'; // horizontal-only pan
      } else if (region === 'y-axis-left') {
        panLockRef.current = 'x'; // vertical-only pan for oxygen
      } else if (region === 'y-axis-right') {
        panLockRef.current = 'x-flux'; // vertical-only pan for flux
      } else if (region === 'plot') {
        panLockRef.current = 'y'; // horizontal-only pan in plot area (no vertical O2 panning)
      }
    };

    const onMouseMove = (event) => {
      if (!isDraggingRef.current) return;
      event.preventDefault();

      const dx = event.clientX - dragStartRef.current.x;
      const dy = event.clientY - dragStartRef.current.y;

      const newXTrans = { ...dragStartRef.current.xTransform };
      let newYTrans = { ...dragStartRef.current.yTransform };
      let newFluxYTrans = { ...dragStartRef.current.fluxYTransform };
      let newYDomainOverride = dragStartRef.current.yDomainOverride;
      let newFluxYDomainOverride = dragStartRef.current.fluxYDomainOverride;

      // Apply pan with lock if present
      if (!panLockRef.current || panLockRef.current === 'y') {
        newXTrans.translate = dragStartRef.current.xTransform.translate + dx;
        // Constrain translation (prevent panning left beyond start of data, allow panning right beyond end)
        const maxTx = 0; // Prevent panning left beyond start
        const minTx = plotWidth * (1 - newXTrans.k); // Prevent panning too far left when zoomed in
        // Allow negative translate values (panning right) but enforce minTx when it's positive (preventing left pan)
        if (minTx > 0) {
          newXTrans.translate = Math.max(minTx, Math.min(maxTx, newXTrans.translate));
        } else {
          // When zoomed in, minTx is negative, so only enforce maxTx (prevent left panning)
          newXTrans.translate = Math.min(maxTx, newXTrans.translate);
        }
      }

      if (!panLockRef.current || panLockRef.current === 'x') {
        // Pan oxygen Y-axis
        // Always use domain shifting for vertical panning (allows infinite panning)
        let baseDomain;
        let tempBaseYScale;
        if (dragStartRef.current.yDomainOverride) {
          baseDomain = dragStartRef.current.yDomainOverride;
          tempBaseYScale = d3
            .scaleLinear()
            .domain(dragStartRef.current.yDomainOverride)
            .range([plotHeight, 0]);
        } else {
          baseDomain = baseYScale.domain();
          tempBaseYScale = baseYScale;
        }

        const { tempYScale } = computeScalesWithBase(
          dragStartRef.current.xTransform,
          dragStartRef.current.yTransform,
          baseXScale,
          tempBaseYScale,
        );
        const visibleDomain = tempYScale.domain();
        const visibleDomainRange = visibleDomain[1] - visibleDomain[0];
        const domainShift = (dy / plotHeight) * visibleDomainRange;

        const newDomain = [baseDomain[0] + domainShift, baseDomain[1] + domainShift];

        newYDomainOverride = newDomain;
        yDomainOverrideRef.current = newDomain;
        pendingYDomainOverrideRef.current = newDomain;
        newYTrans = { ...dragStartRef.current.yTransform };
      }

      if (panLockRef.current === 'x-flux') {
        // Pan flux Y-axis only
        let baseDomain;
        let tempBaseFluxYScale;
        if (dragStartRef.current.fluxYDomainOverride) {
          baseDomain = dragStartRef.current.fluxYDomainOverride;
          tempBaseFluxYScale = d3
            .scaleLinear()
            .domain(dragStartRef.current.fluxYDomainOverride)
            .range([plotHeight, 0]);
        } else {
          baseDomain = baseFluxYScale.domain();
          tempBaseFluxYScale = baseFluxYScale;
        }

        const { tempFluxYScale } = computeFluxScaleWithBase(
          dragStartRef.current.xTransform,
          dragStartRef.current.fluxYTransform,
          baseXScale,
          tempBaseFluxYScale,
        );
        const visibleDomain = tempFluxYScale.domain();
        const visibleDomainRange = visibleDomain[1] - visibleDomain[0];
        const domainShift = (dy / plotHeight) * visibleDomainRange;

        const newDomain = [baseDomain[0] + domainShift, baseDomain[1] + domainShift];

        newFluxYDomainOverride = newDomain;
        fluxYDomainOverrideRef.current = newDomain;
        pendingFluxYDomainOverrideRef.current = newDomain;
        newFluxYTrans = { ...dragStartRef.current.fluxYTransform };
      }

      xTransformRef.current = newXTrans;
      yTransformRef.current = newYTrans;
      fluxYTransformRef.current = newFluxYTrans;
      pendingXTransformRef.current = newXTrans;
      pendingYTransformRef.current = newYTrans;
      pendingFluxYTransformRef.current = newFluxYTrans;

      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = requestAnimationFrame(() => {
        // Compute scales - use domain override if it exists
        let tempBaseYScale = baseYScale;
        const domainToUse = newYDomainOverride ?? dragStartRef.current.yDomainOverride;
        if (domainToUse) {
          tempBaseYScale = d3.scaleLinear().domain(domainToUse).range([plotHeight, 0]);
        }

        let tempBaseFluxYScale = baseFluxYScale;
        const fluxDomainToUse = newFluxYDomainOverride ?? dragStartRef.current.fluxYDomainOverride;
        if (fluxDomainToUse) {
          tempBaseFluxYScale = d3.scaleLinear().domain(fluxDomainToUse).range([plotHeight, 0]);
        }

        const { tempXScale, tempYScale } = computeScalesWithBase(
          newXTrans,
          newYTrans,
          baseXScale,
          tempBaseYScale,
        );
        const { tempXScale: tempXScale2, tempFluxYScale } = computeFluxScaleWithBase(
          newXTrans,
          newFluxYTrans,
          baseXScale,
          tempBaseFluxYScale,
        );
        const renderData = computeVisibleData(tempXScale, data, plotWidth);
        drawCanvasTrace(canvasRef.current, renderData, tempXScale, tempYScale, tempFluxYScale);

        // Update marks
        const [xMin, xMax] = tempXScale.domain();
        const xMinMs = xMin.getTime();
        const xMaxMs = xMax.getTime();
        const visibleMarkKeys = new Set();
        if (Array.isArray(currentMarks)) {
          currentMarks.forEach((m) => {
            const startMs = new Date(m.start).getTime();
            const endMs = new Date(m.end ?? m.start).getTime();
            if (!(endMs < xMinMs || startMs > xMaxMs)) {
              visibleMarkKeys.add(`${startMs}-${endMs}`);
            }
          });
        }
        updateMarkPositions(tempXScale, visibleMarkKeys);

        // Update event positions
        const visibleEventKeys = new Set();
        if (Array.isArray(currentEvents)) {
          currentEvents.forEach((e) => {
            const eventTime = e.time instanceof Date ? e.time : new Date(e.time);
            const eventMs = eventTime.getTime();
            if (eventMs >= xMinMs && eventMs <= xMaxMs) {
              visibleEventKeys.add(`${eventMs}`);
            }
          });
        }
        updateEventPositions(tempXScale, visibleEventKeys);

        // Throttled axis updates (only update every ~33ms to reduce flickering)
        const now = performance.now();
        const shouldUpdate = now - lastAxisUpdateRef.current >= 33;

        if (shouldUpdate) {
          if (updateSVGAxesRef.current) {
            updateSVGAxesRef.current(tempXScale, tempYScale, tempFluxYScale);
          }
          lastAxisUpdateRef.current = now;

          // Update global store for X-axis sync (like wheel zoom does)
          // skipNextSyncRef prevents this graph from reacting to its own update
          skipNextSyncRef.current = true;
          setSyncXTransform(newXTrans);
        }
      });
    };

    const onMouseUp = (event) => {
      if (!isDraggingRef.current) return;
      isDraggingRef.current = false;
      panLockRef.current = null;
      setIsLiveSyncing(false); // Signal that live sync has ended

      // Commit pending transforms to React state
      if (pendingXTransformRef.current && pendingYTransformRef.current) {
        // Update global store for X-axis (synced between plots)
        // Don't skip sync - we want active plot to read from store after drag completes
        setSyncXTransform(pendingXTransformRef.current);
        // Also update local state directly (will sync from store on next effect run)
        setXTransform(pendingXTransformRef.current);
        // Y-axes remain local
        setYTransform(pendingYTransformRef.current);
        if (pendingFluxYTransformRef.current) {
          setFluxYTransform(pendingFluxYTransformRef.current);
        }
        pendingXTransformRef.current = null;
        pendingYTransformRef.current = null;
        pendingFluxYTransformRef.current = null;
      }

      // Commit pending Y domain overrides if we modified them during drag
      if (pendingYDomainOverrideRef.current !== null) {
        setYDomainOverride(pendingYDomainOverrideRef.current);
        pendingYDomainOverrideRef.current = null;
      }
      if (pendingFluxYDomainOverrideRef.current !== null) {
        setFluxYDomainOverride(pendingFluxYDomainOverrideRef.current);
        pendingFluxYDomainOverrideRef.current = null;
      }

      // Restore cursor info if mouse ends within plot
      try {
        const rect = svgEl.getBoundingClientRect();
        const localX = event.clientX - rect.left - margin.left;
        const localY = event.clientY - rect.top - margin.top;
        if (localX >= 0 && localX <= plotWidth && localY >= 0 && localY <= plotHeight) {
          const { tempXScale } = computeScales(xTransformRef.current, yTransformRef.current);
          const targetTime = tempXScale.invert(localX);
          if (data.length > 0) {
            const bisector = d3.bisector((d) => d.time).left;
            const index = bisector(data, targetTime);
            const closestPoint = data[Math.min(index, data.length - 1)];
            if (closestPoint) {
              const cursorData = {
                x: localX,
                y: localY,
                time: targetTime,
                oxygen: closestPoint.oxygen,
              };
              if (closestPoint.flux !== undefined) {
                cursorData.flux = closestPoint.flux;
              }
              setCursorInfo(cursorData);
            }
          }
        }
      } catch {}

      // Redraw SVG
      cancelAnimationFrame(zoomEndRafRef.current);
      zoomEndRafRef.current = requestAnimationFrame(() => {
        if (renderSVGRef.current) renderSVGRef.current();
      });
    };

    // Mouse move handler for cursor info (when not dragging)
    const onMouseMoveForCursor = (event) => {
      if (isDraggingRef.current) return;

      const region = getRegion(event.clientX, event.clientY);
      if (region !== 'plot') {
        setCursorInfo(null);
        return;
      }

      const rect = svgEl.getBoundingClientRect();
      const mouseX = event.clientX - rect.left - margin.left;
      const mouseY = event.clientY - rect.top - margin.top;

      const { tempXScale } = computeScales(xTransformRef.current, yTransformRef.current);
      const targetTime = tempXScale.invert(mouseX);

      if (data.length > 0) {
        const bisector = d3.bisector((d) => d.time).left;
        const index = bisector(data, targetTime);
        const closestPoint = data[Math.min(index, data.length - 1)];
        if (closestPoint) {
          const cursorData = {
            x: mouseX,
            y: mouseY,
            time: targetTime,
            oxygen: closestPoint.oxygen,
          };
          if (closestPoint.flux !== undefined) {
            cursorData.flux = closestPoint.flux;
          }
          setCursorInfo(cursorData);
        }
      }
    };

    // Attach event listeners
    svgEl.addEventListener('wheel', onWheel, { passive: false });
    svgEl.addEventListener('mousedown', onMouseDown);
    svgEl.addEventListener('mousemove', onMouseMoveForCursor);
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);

    return () => {
      svgEl.removeEventListener('wheel', onWheel);
      svgEl.removeEventListener('mousedown', onMouseDown);
      svgEl.removeEventListener('mousemove', onMouseMoveForCursor);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, [
    plotWidth,
    plotHeight,
    baseXScale,
    baseYScale,
    baseFluxYScale,
    data,
    currentMarks,
    currentEvents,
    dimensions,
    margin,
    computeVisibleData,
    drawCanvasTrace,
    updateMarkPositions,
    updateEventPositions,
    setSyncXTransform,
    setIsLiveSyncing,
    isActive,
    hasFluxData,
  ]);

  // ═══════════════════════════════════════════════════════════════════════════
  // RENDER EFFECTS - Update canvas/SVG on state changes
  // ═══════════════════════════════════════════════════════════════════════════

  // Build SVG structure when dimensions change or when new data is loaded
  // Full SVG rebuild is expensive (it tears down and re-creates every element,
  // including re-decoding the background pattern + logo images). It must only
  // run on STRUCTURAL changes — first render once data exists, or a size
  // change — NOT on every appended data point. During live recording `data`
  // changes ~once per second; rebuilding the whole SVG each time froze the
  // axis/"visible range" label (the canvas trace kept animating via rAF, which
  // is why only the label appeared to stutter). Live appends are handled
  // incrementally by the lightweight effect below (canvas trace + updateSVGAxes).
  useEffect(() => {
    if (dimensions.width === 0 || dimensions.height === 0) return;
    if (!data.length) return;

    const dimsChanged =
      prevDimsRef.current.w !== dimensions.width || prevDimsRef.current.h !== dimensions.height;

    if (svgBuiltRef.current && !dimsChanged) {
      // Data-only change (live append): skip the full rebuild.
      return;
    }

    renderSVG();
    renderCanvas();
    svgBuiltRef.current = true;
    prevDimsRef.current = { w: dimensions.width, h: dimensions.height };
  }, [dimensions.width, dimensions.height, data, renderSVG, renderCanvas]);

  // Update canvas trace, marks, and axes on wheel zoom (drag handles updates in zoom handler)
  // Skip this effect during drag for active plot to prevent flickering
  useEffect(() => {
    if (dimensions.width === 0 || dimensions.height === 0) return;

    // Skip rendering if this is the active plot and we're currently dragging
    // The drag handler already handles rendering via requestAnimationFrame
    if (isActive && isDraggingRef.current) {
      return;
    }

    cancelAnimationFrame(rafIdRef.current);
    rafIdRef.current = requestAnimationFrame(() => {
      renderCanvas();

      // Update mark positions (only during wheel zoom - drag handles this differently)
      if (visibleMarks.length > 0) {
        const visibleSet = new Set(
          visibleMarks.map(
            (m) => `${new Date(m.start).getTime()}-${new Date(m.end ?? m.start).getTime()}`,
          ),
        );
        updateMarkPositions(xScale, visibleSet);
      }

      // Update event positions
      if (visibleEvents.length > 0) {
        const visibleEventSet = new Set(
          visibleEvents.map((e) => {
            const eventTime = e.time instanceof Date ? e.time : new Date(e.time);
            return `${eventTime.getTime()}`;
          }),
        );
        updateEventPositions(xScale, visibleEventSet);
      }

      // Update axes/grids in real-time during wheel zoom
      updateSVGAxes(xScale, yScale, fluxYScale);
    });
    return () => cancelAnimationFrame(rafIdRef.current);
  }, [
    xScale,
    yScale,
    fluxYScale,
    renderCanvas,
    updateMarkPositions,
    updateEventPositions,
    updateSVGAxes,
    dimensions.width,
    dimensions.height,
    visibleMarks,
    visibleEvents,
    isActive,
  ]);

  // Reset viewport/zoom when a new file is opened
  useEffect(() => {
    if (!currentFileMeta?.id) return;
    // Clear zoom state and overrides
    setYDomainOverride(null);
    setFluxYDomainOverride(null);
    // Reset global X-axis navigation (synced between plots)
    skipNextSyncRef.current = true;
    resetSyncNavigation();
    setXDomainOverride(null);
    setCursorInfo(null);
    setXTransform({ k: 1, translate: 0 });
    setYTransform({ k: 1, translate: 0 });
    setFluxYTransform({ k: 1, translate: 0 });
    xTransformRef.current = { k: 1, translate: 0 };
    yTransformRef.current = { k: 1, translate: 0 };
    fluxYTransformRef.current = { k: 1, translate: 0 };
    renderOverlaySVG();
  }, [currentFileMeta?.id, resetSyncNavigation]);

  // Cursor overlay renders only on cursor moves or size changes
  useEffect(() => {
    if (dimensions.width === 0 || dimensions.height === 0) return;
    renderOverlaySVG();
  }, [dimensions, cursorInfo, renderOverlaySVG]);

  // clear cursor info if pointer moves outside container bounds
  useEffect(() => {
    const onGlobalPointerMove = (e) => {
      const el = containerRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const x = e.clientX;
      const y = e.clientY;
      if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
        if (cursorInfo) setCursorInfo(null);
      }
    };
    window.addEventListener('pointermove', onGlobalPointerMove, { passive: true });
    return () => window.removeEventListener('pointermove', onGlobalPointerMove);
  }, []);

  // ═══════════════════════════════════════════════════════════════════════════
  // CONTROL FUNCTIONS - Exposed to parent via ref
  // ═══════════════════════════════════════════════════════════════════════════

  const resetZoom = useCallback(() => {
    setYDomainOverride(null);
    setFluxYDomainOverride(null);
    // Reset global X-axis navigation (synced between plots)
    skipNextSyncRef.current = true;
    resetSyncNavigation();
    setXDomainOverride(null);
    setXTransform({ k: 1, translate: 0 });
    setYTransform({ k: 1, translate: 0 });
    setFluxYTransform({ k: 1, translate: 0 });
    xTransformRef.current = { k: 1, translate: 0 };
    yTransformRef.current = { k: 1, translate: 0 };
    fluxYTransformRef.current = { k: 1, translate: 0 };
  }, [resetSyncNavigation]);

  const resetXAxis = useCallback(() => {
    // Reset X to full data domain (identity transform) - preserve Y transform
    // Update global store for X-axis (synced between plots)
    const resetTransform = { k: 1, translate: 0 };
    skipNextSyncRef.current = true;
    setSyncXTransform(resetTransform);
    setSyncXDomainOverride(null);
    setXDomainOverride(null);
    setXTransform(resetTransform);
    xTransformRef.current = resetTransform;
    // Keep Y transform unchanged to preserve Y-axis zoom/pan state
    // Y domain override is also preserved to maintain Y-axis base domain
  }, [setSyncXTransform, setSyncXDomainOverride]);

  // Expose controls
  useEffect(() => {
    if (controlsRef) {
      controlsRef.current = { resetZoom, resetXAxis };
    }
  }, [controlsRef, resetZoom, resetXAxis]);

  // Loading / error / no data available
  if (isLoading) {
    return (
      <Box
        sx={{
          p: 4,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          backgroundColor: theme.palette.porcelain[50],
          marginLeft: `${margin.left}px`,
          marginRight: `${margin.right}px`,
          marginTop: `${margin.top}px`,
          marginBottom: `${margin.bottom}px`,
          backgroundImage: `url(${graphBackgroundPattern})`,
        }}
      >
        <CircularProgress size={24} sx={{ mb: 2 }} />
        <Typography variant="body1">Loading plot data...</Typography>
      </Box>
    );
  }
  if (error) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          height: '100%',
        }}
      >
        <TriangleAlert size={24} strokeWidth={1.5} stroke={theme.palette.cranberry[600]} />
        <Typography variant="body1" sx={{ color: theme.palette.cranberry[600] }}>
          Error loading plot data
        </Typography>
      </Box>
    );
  }
  if (!currentFileData) {
    return (
      <Box
        sx={{
          p: 4,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
        }}
      >
        <Typography variant="h6" sx={{ mb: 1 }}>
          No data available
        </Typography>
        <Typography variant="body2" sx={{ textAlign: 'center' }}>
          Please open or upload a DLD8 file first to view the plot.
        </Typography>
      </Box>
    );
  }

  const containerSx = {
    position: 'relative',
    width: '100%',
    overflow: 'hidden',
    borderRadius: 1,
    backgroundRepeat: 'repeat',
    height: height,
  };

  return (
    <Stack direction="column" spacing={1} sx={{ height: '100%' }}>
      <Box ref={containerRef} sx={containerSx} onMouseLeave={() => setCursorInfo(null)}>
        {/* Bottom SVG (axes + background) */}
        <svg
          ref={svgRef}
          style={{ position: 'absolute', top: 0, left: 0, zIndex: 0, pointerEvents: 'all' }}
        />
        {/* Canvas (trace) */}
        <canvas
          ref={canvasRef}
          style={{ position: 'absolute', top: 0, left: 0, zIndex: 1, pointerEvents: 'none' }}
        />
        {/* Top overlay SVG (cursor visuals) */}
        <svg
          ref={overlaySvgRef}
          style={{ position: 'absolute', top: 0, left: 0, zIndex: 2, pointerEvents: 'none' }}
        />
      </Box>
    </Stack>
  );
};

export default Graph;
